import { useState } from 'react';
import { AlertTriangle, Info, X, Filter, ChevronRight, Eye, EyeOff } from 'lucide-react';
import clsx from 'clsx';
import type { ValidationWarning, ValidationWarningType, ValidationWarningSeverity } from '../../hooks/useWarnings';

interface WarningPanelProps {
  warnings: ValidationWarning[];
  onDismiss: (warningId: string) => void;
  onNavigateToSection: (sectionId: string) => void;
  onClose: () => void;
}

/**
 * Pannello per visualizzare e gestire i warnings di validazione
 *
 * Features:
 * - Lista warnings con severità e tipo
 * - Filtri per tipo di warning
 * - Click → navigazione alla sezione problematica
 * - Dismiss warnings (localStorage)
 * - Contatori per tipo e severity
 */
export function WarningPanel({ warnings, onDismiss, onNavigateToSection, onClose }: WarningPanelProps) {
  const [filterType, setFilterType] = useState<ValidationWarningType | 'all'>('all');
  const [filterSeverity, setFilterSeverity] = useState<ValidationWarningSeverity | 'all'>('all');

  // Filtra warnings
  const filteredWarnings = warnings.filter((w) => {
    if (filterType !== 'all' && w.type !== filterType) return false;
    if (filterSeverity !== 'all' && w.severity !== filterSeverity) return false;
    return true;
  });

  // Statistiche
  const stats = {
    total: warnings.length,
    warnings: warnings.filter(w => w.severity === 'warning').length,
    info: warnings.filter(w => w.severity === 'info').length,
    byType: warnings.reduce((acc, w) => {
      acc[w.type] = (acc[w.type] || 0) + 1;
      return acc;
    }, {} as Record<ValidationWarningType, number>),
  };

  const handleDismiss = (warningId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Previeni navigazione
    onDismiss(warningId);
  };

  const handleWarningClick = (warning: ValidationWarning) => {
    if (warning.sectionId) {
      onNavigateToSection(warning.sectionId);
      onClose(); // Chiudi pannello dopo navigazione
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              <h2 className="text-lg font-semibold text-gray-900">
                Avvisi di Validazione
              </h2>
            </div>
            <div className="flex items-center space-x-2 text-sm">
              <span className="px-2 py-1 bg-gray-100 rounded text-gray-700">
                {filteredWarnings.length} di {warnings.length}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
            aria-label="Chiudi pannello avvisi"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Stats Summary */}
        <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center space-x-4 text-sm">
            <div className="flex items-center space-x-1.5">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              <span className="text-gray-700">
                <span className="font-semibold">{stats.warnings}</span> avvertimenti
              </span>
            </div>
            <div className="flex items-center space-x-1.5">
              <Info className="w-4 h-4 text-blue-600" />
              <span className="text-gray-700">
                <span className="font-semibold">{stats.info}</span> info
              </span>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Filter className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Filtri:</span>

            {/* Filter by Type */}
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as ValidationWarningType | 'all')}
              className="input-sm text-sm"
            >
              <option value="all">Tutti i tipi ({warnings.length})</option>
              <option value="no-variants">
                Nessuna variante ({stats.byType['no-variants'] || 0})
              </option>
              <option value="many-variants">
                Molte varianti ({stats.byType['many-variants'] || 0})
              </option>
              <option value="similar-ids">
                IDs simili ({stats.byType['similar-ids'] || 0})
              </option>
              <option value="unused-slot">
                Slot non usati ({stats.byType['unused-slot'] || 0})
              </option>
            </select>

            {/* Filter by Severity */}
            <select
              value={filterSeverity}
              onChange={(e) => setFilterSeverity(e.target.value as ValidationWarningSeverity | 'all')}
              className="input-sm text-sm"
            >
              <option value="all">Tutte le severity ({warnings.length})</option>
              <option value="warning">Avvertimenti ({stats.warnings})</option>
              <option value="info">Info ({stats.info})</option>
            </select>

            {/* Clear Filters */}
            {(filterType !== 'all' || filterSeverity !== 'all') && (
              <button
                onClick={() => {
                  setFilterType('all');
                  setFilterSeverity('all');
                }}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                Cancella filtri
              </button>
            )}
          </div>
        </div>

        {/* Warnings List */}
        <div className="flex-1 overflow-y-auto">
          {filteredWarnings.length === 0 ? (
            <div className="text-center py-12 px-6">
              {warnings.length === 0 ? (
                <>
                  <Eye className="w-12 h-12 text-green-500 mx-auto mb-3" />
                  <p className="text-lg font-medium text-gray-900">Nessun avviso!</p>
                  <p className="text-sm text-gray-500 mt-1">
                    Tutte le sezioni sono valide
                  </p>
                </>
              ) : (
                <>
                  <Filter className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-lg font-medium text-gray-900">Nessun risultato</p>
                  <p className="text-sm text-gray-500 mt-1">
                    Nessun avviso corrisponde ai filtri selezionati
                  </p>
                </>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredWarnings.map((warning) => (
                <WarningItem
                  key={warning.id}
                  warning={warning}
                  onClick={() => handleWarningClick(warning)}
                  onDismiss={(e) => handleDismiss(warning.id, e)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
          <p className="text-xs text-gray-500">
            <Info className="w-3 h-3 inline mr-1" aria-hidden="true" />
            Gli avvisi sono suggerimenti non bloccanti. Puoi salvare anche con avvisi attivi.
          </p>
        </div>
      </div>
    </div>
  );
}

interface WarningItemProps {
  warning: ValidationWarning;
  onClick: () => void;
  onDismiss: (e: React.MouseEvent) => void;
}

function WarningItem({ warning, onClick, onDismiss }: WarningItemProps) {
  const isWarning = warning.severity === 'warning';
  const hasSection = !!warning.sectionId;

  // Icona e colori basati su severity
  const Icon = isWarning ? AlertTriangle : Info;
  const colors = {
    bg: isWarning ? 'bg-amber-50 hover:bg-amber-100' : 'bg-blue-50 hover:bg-blue-100',
    border: isWarning ? 'border-l-amber-500' : 'border-l-blue-500',
    icon: isWarning ? 'text-amber-600' : 'text-blue-600',
    badge: isWarning ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700',
  };

  // Label tipo warning
  const typeLabels: Record<ValidationWarningType, string> = {
    'no-variants': 'Nessuna Variante',
    'many-variants': 'Molte Varianti',
    'similar-ids': 'IDs Simili',
    'unused-slot': 'Slot Non Usato',
    'invalid-slot-reference': 'Riferimento Slot Non Valido',
    'invalid-section-reference': 'Riferimento Sezione Non Valido',
    'orphaned-section': 'Sezione Orfana',
  };

  return (
    <div
      className={clsx(
        'border-l-4 transition-colors duration-150',
        colors.border,
        colors.bg,
        hasSection && 'cursor-pointer'
      )}
      onClick={hasSection ? onClick : undefined}
      role={hasSection ? 'button' : undefined}
      tabIndex={hasSection ? 0 : undefined}
    >
      <div className="px-6 py-4 flex items-start space-x-4">
        {/* Icon */}
        <div className="flex-shrink-0 mt-0.5">
          <Icon className={clsx('w-5 h-5', colors.icon)} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2 mb-1">
            <span className={clsx('px-2 py-0.5 rounded text-xs font-medium', colors.badge)}>
              {typeLabels[warning.type]}
            </span>
            {warning.sectionId && (
              <span className="text-xs text-gray-500 font-mono">
                {warning.sectionId}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-900 leading-relaxed">
            {warning.message}
          </p>
          {warning.action && (
            <p className="text-xs text-gray-500 mt-1">
              {warning.action === 'add-variant' && '💡 Suggerimento: Aggiungi almeno una variante'}
              {warning.action === 'review-section' && '💡 Suggerimento: Rivedi la configurazione della sezione'}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center space-x-2 flex-shrink-0">
          {hasSection && (
            <ChevronRight className="w-4 h-4 text-gray-400" aria-hidden="true" />
          )}
          <button
            onClick={onDismiss}
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-white/50"
            title="Nascondi avviso"
            aria-label="Nascondi avviso"
          >
            <EyeOff className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
